import React, { useRef, useState } from 'react';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { ErrorMessage, Field, Formik, FormikProps } from 'formik';
import { default as lodashFirst } from 'lodash/first';
import './index.scss';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { UserAdminService } from '$/services';
import BixButton from '$/components/global/bixButton';
import InputBlock from '$/components/global/inputBlock';
import Modal from '$/components/global/modal';

interface IBulkImportUsers {
  active: boolean;
  clients?: Bixby.Models.ClientSlim[];
  onImportSubmit: () => void;
  onCloseModal: () => void;
}

interface UserValues {
  client_Id: string | null;
  usersFile: File | null;
}

const BulkImportUsers: React.FC<IBulkImportUsers> = props => {
  const { clients, active, onImportSubmit, onCloseModal } = props;

  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(false);
  const [importErrorMessage, setImportErrorMessage] = useState<string>();

  const formikRef = useRef<FormikProps<UserValues>>(null);

  const clientRef = useRef<HTMLInputElement>(null);

  const handleCloseModal = () => {
    setImportError(false);
    setImportErrorMessage(undefined);
    setIsImporting(false);

    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }

    if (clientRef && clientRef.current) {
      clientRef.current.value = '';
    }

    onCloseModal();
  };

  const importUsersAsync = async (values: UserValues) => {
    if (!values.client_Id || !values.usersFile) return;

    try {
      setImportError(false);
      setImportErrorMessage(undefined);
      setIsImporting(true);

      await UserAdminService.importUsers(values.client_Id, values.usersFile);

      if (clientRef && clientRef.current) {
        clientRef.current.value = '';
      }

      if (formikRef && formikRef.current) {
        formikRef.current.resetForm();
      }

      // Closes the Modal
      onImportSubmit();
    } catch (err: any) {
      setImportError(true);
      setImportErrorMessage(err.response ? err.response.data : 'Error importing data');
    } finally {
      setIsImporting(false);
    }
  };

  const getTemplateFile = async () => {
    await UserAdminService.getTemplateFile();
  };

  const getExtension = fileName => {
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1);
    return extension;
  };

  const schema = Yup.object({
    client_Id: Yup.string().required('Client is required'),
    usersFile: Yup.mixed()
      .required('File is required')
      .test({
        message: 'Please provide a supported file type',
        test: (file, context) => {
          const isValid = ['xlsx', 'xls'].includes(getExtension((file as File).name));
          if (!isValid) context?.createError();
          return isValid;
        },
      }),
  });

  const clearForm = () => {
    setImportError(false);
    setIsImporting(false);
    if (formikRef && formikRef.current) {
      formikRef.current.setFieldValue('client_Id', null);
      formikRef.current.setFieldValue('usersFile', null);
    }
  };

  const initialValues: UserValues = {
    client_Id: null,
    usersFile: null,
  };

  return (
    <Modal title="Bulk Insert Users" active={active} forForm onClose={handleCloseModal}>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={async (values: UserValues) => {
          await importUsersAsync(values);
        }}
        render={({ handleSubmit, values, handleBlur }) => (
          <form className="import-user-form" onSubmit={handleSubmit}>
            <Field
              name="client_Id"
              render={({ form }: { [key: string]: any }) => {
                const handleClientChange = (ev: React.FormEvent<HTMLInputElement>) => {
                  if (clients) {
                    const selected = clients.find(c => c.name === ev.currentTarget.value);
                    if (selected) {
                      form.setFieldValue('client_Id', selected.id);
                    } else {
                      clearForm();
                    }
                  } else {
                    clearForm();
                  }
                };
                return (
                  <InputBlock title="Client">
                    <input
                      ref={clientRef}
                      list="clients"
                      id="client_Id_bulk"
                      name="client_Id"
                      onBlur={handleBlur}
                      onChange={handleClientChange}
                      autoComplete="off"
                      disabled={isImporting}
                    />
                    <datalist id="clients">
                      {clients &&
                        clients.map((result, ix) => {
                          return (
                            <option key={ix} label={result.name} value={result.name}>
                              {result.name}
                            </option>
                          );
                        })}
                    </datalist>
                  </InputBlock>
                );
              }}
            />
            <ErrorMessage name="client_Id">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
            {values.client_Id && (
              <>
                <div className="search_block">
                  <Field
                    name="usersFile"
                    render={({ form }: { [key: string]: any }) => {
                      const handleUserFileChange = (ev: React.FormEvent<HTMLInputElement>) => {
                        const file = lodashFirst(ev.currentTarget.files);
                        form.setFieldValue('usersFile', file);
                      };
                      return (
                        <>
                          <div className="search_block_title">{'Import File'}</div>
                          <input name="usersFile" type="file" disabled={isImporting} onChange={handleUserFileChange} />
                          <span className="template_link" onClick={getTemplateFile}>
                            Download Import Template.xlsx
                          </span>
                        </>
                      );
                    }}
                  />
                </div>
                <ErrorMessage name="usersFile">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
              </>
            )}
            <div className="btn_container">
              <BixButton useLoading isLoading={isImporting} disabled={isImporting} type="submit">
                Import Users
              </BixButton>
            </div>
            {!!importError && (
              <>
                <p className="error">{importErrorMessage}</p>
              </>
            )}
          </form>
        )}
      />
    </Modal>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    clients: state.clientState.clients,
  };
};

export default connect(mapStateToProps)(BulkImportUsers);
